import { getDataset } from "./agent-dataset-cache";
import {
  inferChartXAxis,
  inferChartXKey,
  inferOhlcLineSeriesKeys,
  isTimeColumnName,
  sanitizeChartColumns,
} from "./chart-axis-utils";

export type AgentChartType = "pie" | "bar" | "line";

export type AgentChartYAxisSide = "left" | "right";

export interface AgentChartInput {
  datasetId: string;
  chartType: AgentChartType;
  labelKey: string;
  valueKey: string;
  seriesKeys?: string[];
  xKey?: string;
  title?: string;
  xAxis?: "category" | "time" | "index";
  /** Per-series Y axis when scales differ (e.g. GOLD left, SILVER right). */
  seriesScale?: Record<string, AgentChartYAxisSide>;
  /** Shorthand: dual = first series left, rest right when magnitudes differ. */
  yAxis?: "left" | "right" | "dual";
}

function inferNumericSeriesColumns(
  columns: string[],
  exclude: Set<string>,
): string[] {
  return inferOhlcLineSeriesKeys(columns, exclude);
}

function inferValueKey(
  ds: NonNullable<ReturnType<typeof getDataset>>,
  labelKey: string,
  xKey: string,
): string | undefined {
  const exclude = new Set([labelKey, xKey]);
  const candidates = ds.columns.filter((c) => !exclude.has(c));
  return candidates[0];
}

export function resolveChartSeriesKeys(
  ds: NonNullable<ReturnType<typeof getDataset>>,
  spec: {
    chartType: AgentChartType;
    labelKey: string;
    xKey: string;
    seriesKeys?: string[];
  },
): string[] {
  if (spec.chartType !== "line") return [];

  const columns = sanitizeChartColumns(ds.columns);
  const seriesFromInput = spec.seriesKeys?.filter(
    (k): k is string => typeof k === "string" && k.trim() !== "" && columns.includes(k),
  );
  if (seriesFromInput?.length) return seriesFromInput;

  const exclude = new Set([
    spec.labelKey,
    spec.xKey,
    "label",
    "x",
    ...columns.filter(isTimeColumnName),
  ]);
  const numericCols = inferNumericSeriesColumns(columns, exclude);

  if (numericCols.length >= 2) return numericCols;
  if (numericCols.length === 1) return numericCols;
  return [];
}

export function validateAgentChartInput(
  raw: unknown,
): { ok: true; spec: AgentChartInput } | { ok: false; error: string } {
  if (raw == null || typeof raw !== "object") {
    return { ok: false, error: "Invalid chart input" };
  }
  const o = raw as Record<string, unknown>;
  const datasetId = typeof o.datasetId === "string" ? o.datasetId.trim() : "";
  const chartType = o.chartType;
  let labelKey = typeof o.labelKey === "string" ? o.labelKey.trim() : "";
  let valueKey = typeof o.valueKey === "string" ? o.valueKey.trim() : "";

  if (!datasetId) return { ok: false, error: "datasetId is required" };
  if (chartType !== "pie" && chartType !== "bar" && chartType !== "line") {
    return { ok: false, error: "chartType must be pie, bar, or line" };
  }
  if (!labelKey && chartType !== "line") {
    return { ok: false, error: "labelKey is required for pie and bar charts" };
  }

  const ds = getDataset(datasetId);
  if (!ds) return { ok: false, error: `Dataset not found: ${datasetId}` };

  const columns = sanitizeChartColumns(ds.columns);
  const cols = new Set(columns);
  if (!labelKey) {
    // Line charts label points along the x axis — default to the (given or inferred) xKey.
    labelKey =
      typeof o.xKey === "string" && o.xKey.trim()
        ? o.xKey.trim()
        : inferChartXKey(columns, "");
  }
  if (!cols.has(labelKey)) {
    return { ok: false, error: `labelKey "${labelKey}" not in dataset columns` };
  }

  let xKey =
    typeof o.xKey === "string" && o.xKey.trim()
      ? o.xKey.trim()
      : inferChartXKey(columns, labelKey);
  if (!cols.has(xKey)) {
    return { ok: false, error: `xKey "${xKey}" not in dataset columns` };
  }

  const seriesKeysInput = Array.isArray(o.seriesKeys)
    ? o.seriesKeys.filter((k): k is string => typeof k === "string" && k.trim() !== "")
    : undefined;

  const dsForResolve = { ...ds, columns };
  const seriesKeys = resolveChartSeriesKeys(dsForResolve, {
    chartType,
    labelKey,
    xKey,
    seriesKeys: seriesKeysInput,
  });

  const multiLine = chartType === "line" && seriesKeys.length > 0;

  if (!valueKey) {
    if (multiLine) {
      valueKey = seriesKeys[0]!;
    } else if (chartType === "pie" || chartType === "bar") {
      const inferred = inferValueKey(dsForResolve, labelKey, xKey);
      if (!inferred) {
        return { ok: false, error: "valueKey is required for pie and bar charts" };
      }
      valueKey = inferred;
    } else {
      return { ok: false, error: "valueKey is required (or provide seriesKeys for multi-line)" };
    }
  }

  if (!cols.has(valueKey) && !multiLine) {
    return { ok: false, error: `valueKey "${valueKey}" not in dataset columns` };
  }

  if (seriesKeys.length) {
    for (const k of seriesKeys) {
      if (!cols.has(k)) {
        return { ok: false, error: `seriesKey "${k}" not in dataset columns` };
      }
    }
  }

  const xAxisInput = o.xAxis;
  if (
    xAxisInput != null &&
    xAxisInput !== "category" &&
    xAxisInput !== "time" &&
    xAxisInput !== "index"
  ) {
    return { ok: false, error: "xAxis must be category, time, or index" };
  }

  const xAxis = inferChartXAxis(
    xKey,
    xAxisInput as AgentChartInput["xAxis"] | undefined,
  );

  const yAxisInput = o.yAxis;
  if (
    yAxisInput != null &&
    yAxisInput !== "left" &&
    yAxisInput !== "right" &&
    yAxisInput !== "dual"
  ) {
    return { ok: false, error: "yAxis must be left, right, or dual" };
  }

  let seriesScale: Record<string, AgentChartYAxisSide> | undefined;
  if (o.seriesScale != null && typeof o.seriesScale === "object") {
    seriesScale = {};
    for (const [k, v] of Object.entries(o.seriesScale as Record<string, unknown>)) {
      if (v === "left" || v === "right") seriesScale[k] = v;
    }
  }

  return {
    ok: true,
    spec: {
      datasetId,
      chartType,
      labelKey,
      valueKey,
      seriesKeys: multiLine ? seriesKeys : undefined,
      xKey,
      title: typeof o.title === "string" ? o.title : undefined,
      xAxis,
      seriesScale,
      yAxis: yAxisInput as AgentChartInput["yAxis"] | undefined,
    },
  };
}
